import React, { useState, useEffect } from 'react';
// FIX: Corrected Firebase User type usage.
import firebase from 'firebase/compat/app';
import { Transaction, Settings } from '../types';
import { functions } from '../services/firebase';
import { getStripe } from '../services/stripe';


interface ProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: firebase.User | null;
    onUpdateProfile: (newAlias: string) => Promise<void>;
    onChangePassword: (newPassword: string) => Promise<void>;
    onOpenDeleteAccountModal: () => void;
    onOpenPricingModal: () => void;
    onUpdateCurrency: (newCurrency: string) => Promise<void>;
    transactions: Transaction[];
    settings: Settings;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, user, onUpdateProfile, onChangePassword, onOpenDeleteAccountModal, onOpenPricingModal, onUpdateCurrency, transactions, settings }) => {
    // --- Guard Clause ---
    // If the modal isn't open or essential data is missing, don't render anything.
    // This prevents errors if the component re-renders while data is still loading.
    if (!isOpen || !user || !settings) {
        return null;
    }

    const [alias, setAlias] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [currency, setCurrency] = useState('EUR');
    
    const [aliasMessage, setAliasMessage] = useState({ type: '', text: '' });
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
    const [currencyMessage, setCurrencyMessage] = useState({ type: '', text: '' });
    
    const [isAliasLoading, setIsAliasLoading] = useState(false);
    const [isPasswordLoading, setIsPasswordLoading] = useState(false);
    const [isCurrencyLoading, setIsCurrencyLoading] = useState(false);
    const [isPortalLoading, setIsPortalLoading] = useState(false);

    useEffect(() => {
        // The guard clause above ensures user and settings are available here
        setAlias(user.displayName || '');
        setCurrency(settings.currency || 'EUR');
        
        // Reset state on open
        setAliasMessage({ type: '', text: '' });
        setPasswordMessage({ type: '', text: '' });
        setCurrencyMessage({ type: '', text: '' });
        setNewPassword('');
        setConfirmPassword('');
        setIsAliasLoading(false);
        setIsPasswordLoading(false);
        setIsCurrencyLoading(false);
        setIsPortalLoading(false);
    }, [user, settings, isOpen]);

    const handleAliasSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsAliasLoading(true);
        setAliasMessage({ type: '', text: '' });
        
        if (!alias.trim()) {
            setAliasMessage({ type: 'error', text: 'El alias no puede estar vacío.' });
            setIsAliasLoading(false);
            return;
        }

        try {
            await onUpdateProfile(alias);
            setAliasMessage({ type: 'success', text: 'Alias actualizado correctamente.' });
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            console.error(error);
            setAliasMessage({ type: 'error', text: 'Error al actualizar el alias.' });
            setIsAliasLoading(false);
        }
    };
    
    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsPasswordLoading(true);
        setPasswordMessage({ type: '', text: '' });
        
        if (newPassword.length < 6) {
            setPasswordMessage({ type: 'error', text: 'La contraseña debe tener al menos 6 caracteres.' });
            setIsPasswordLoading(false);
            return;
        }
        
        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'Las contraseñas no coinciden.' });
            setIsPasswordLoading(false);
            return;
        }

        try {
            await onChangePassword(newPassword);
            setPasswordMessage({ type: 'success', text: 'Contraseña actualizada correctamente.' });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error(error);
            setPasswordMessage({ type: 'error', text: 'Error al actualizar la contraseña. Puede que necesites volver a iniciar sesión.' });
        } finally {
            setIsPasswordLoading(false);
        }
    };
    
    const handleCurrencySubmit = async () => {
        setIsCurrencyLoading(true);
        setCurrencyMessage({ type: '', text: '' });
        try {
            await onUpdateCurrency(currency);
            setCurrencyMessage({ type: 'success', text: 'Moneda actualizada correctamente.' });
        } catch (error) {
            console.error(error);
            setCurrencyMessage({ type: 'error', text: 'Error al actualizar la moneda.' });
        } finally {
            setIsCurrencyLoading(false);
        }
    };
    
    const handleManageSubscription = async () => {
        if (!functions) return;

        // If user is on trial, open the pricing modal to subscribe
        if (settings.subscriptionStatus === 'trial') {
            onOpenPricingModal();
            return;
        }

        setIsPortalLoading(true);
        try {
            const createPortal = functions.httpsCallable('createStripePortal');
            const result = await createPortal({ returnUrl: window.location.href });
            const portalUrl = (result.data as any).url;
            window.location.href = portalUrl;
        } catch (error) {
            console.error("Error creating portal session:", error);
            alert("No se pudo abrir el portal de gestión. Por favor, inténtalo de nuevo.");
            setIsPortalLoading(false);
        }
    };

    const getSubscriptionStatusText = () => {
        switch (settings.subscriptionStatus) {
            case 'trial': return 'Prueba';
            case 'active': return 'Activa';
            case 'past_due': return 'Pago Pendiente';
            case 'canceled': return 'Cancelada';
            default: return 'Gratuita';
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-start sm:items-center p-4" onClick={onClose}>
            <div className="bg-light-surface dark:bg-surface rounded-lg shadow-xl w-full max-w-lg border border-light-border dark:border-border flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-light-border dark:border-border flex-shrink-0">
                    <h2 className="text-2xl font-bold text-light-text-primary dark:text-text-primary">Mi Perfil</h2>
                    <button
                        onClick={onClose}
                        className="text-light-text-secondary dark:text-text-secondary hover:text-light-text-primary dark:hover:text-text-primary transition-colors"
                        aria-label="Cerrar modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto p-6">
                    {/* Update Alias Form */}
                    <form onSubmit={handleAliasSubmit} className="space-y-4">
                        <h3 className="text-lg font-semibold text-light-text-primary dark:text-text-primary">Cambiar Alias</h3>
                        <div>
                            <label htmlFor="alias" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Alias</label>
                            <input type="text" id="alias" value={alias} onChange={e => setAlias(e.target.value)} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        {aliasMessage.text && (
                            <p className={`text-sm ${aliasMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{aliasMessage.text}</p>
                        )}
                        <div className="flex justify-end">
                            <button type="submit" disabled={isAliasLoading} className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold transition-colors disabled:bg-gray-600">
                                {isAliasLoading ? 'Guardando...' : 'Guardar Alias'}
                            </button>
                        </div>
                    </form>

                    {/* Currency Preferences */}
                    <div className="space-y-4 pt-6 mt-6 border-t border-light-border dark:border-border">
                        <h3 className="text-lg font-semibold text-light-text-primary dark:text-text-primary">Preferencias de Moneda</h3>
                        <div>
                            <label htmlFor="currency" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Moneda Principal</label>
                            <select 
                                id="currency" 
                                value={currency} 
                                onChange={e => setCurrency(e.target.value)}
                                disabled={transactions.length > 0 || isCurrencyLoading}
                                className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="EUR">Euro (€)</option>
                                <option value="USD">Dólar Americano ($)</option>
                                <option value="MXN">Peso Mexicano ($)</option>
                                <option value="COP">Peso Colombiano ($)</option>
                                <option value="ARS">Peso Argentino ($)</option>
                            </select>
                            {transactions.length > 0 && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                                    No se puede cambiar la moneda porque ya tienes transacciones registradas. Esto es para mantener la consistencia de tus datos.
                                </p>
                            )}
                        </div>
                        {currencyMessage.text && (
                            <p className={`text-sm ${currencyMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{currencyMessage.text}</p>
                        )}
                        <div className="flex justify-end">
                            <button 
                                type="button" 
                                onClick={handleCurrencySubmit} 
                                disabled={isCurrencyLoading || transactions.length > 0 || currency === settings.currency} 
                                className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold transition-colors disabled:bg-gray-600 disabled:cursor-not-allowed">
                                {isCurrencyLoading ? 'Guardando...' : 'Guardar Moneda'}
                            </button>
                        </div>
                    </div>

                    {/* Change Password Form */}
                    <form onSubmit={handlePasswordSubmit} className="space-y-4 pt-6 mt-6 border-t border-light-border dark:border-border">
                        <h3 className="text-lg font-semibold text-light-text-primary dark:text-text-primary">Cambiar Contraseña</h3>
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Nueva Contraseña</label>
                            <input type="password" id="newPassword" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">Confirmar Nueva Contraseña</label>
                            <input type="password" id="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full bg-light-background dark:bg-background border border-light-border dark:border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        {passwordMessage.text && (
                            <p className={`text-sm ${passwordMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>{passwordMessage.text}</p>
                        )}
                        <div className="flex justify-end">
                            <button type="submit" disabled={isPasswordLoading} className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold transition-colors disabled:bg-gray-600">
                                {isPasswordLoading ? 'Actualizando...' : 'Actualizar Contraseña'}
                            </button>
                        </div>
                    </form>

                     {/* Subscription Info */}
                    <div className="space-y-4 pt-6 mt-6 border-t border-light-border dark:border-border">
                        <h3 className="text-lg font-semibold text-light-text-primary dark:text-text-primary">Suscripción y Facturación</h3>
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-light-text-secondary dark:text-text-secondary">Estado de tu cuenta:</span>
                            <span className="px-3 py-1 text-sm font-semibold rounded-full bg-secondary/20 text-secondary">
                                {getSubscriptionStatusText()}
                            </span>
                        </div>
                        {settings.subscriptionStatus === 'trial' && settings.trialEndDate && (
                            <p className="text-sm text-center text-light-text-secondary dark:text-text-secondary">
                                Tu prueba termina el {new Date(settings.trialEndDate).toLocaleDateString('es-ES')}.
                            </p>
                        )}
                         {settings.subscriptionStatus === 'active' && settings.subscriptionEndDate && (
                            <p className="text-sm text-center text-light-text-secondary dark:text-text-secondary">
                                Tu plan se renovará el {new Date(settings.subscriptionEndDate).toLocaleDateString('es-ES')}.
                            </p>
                        )}
                        <div className="flex flex-col items-end mt-4">
                            <button
                                onClick={handleManageSubscription}
                                disabled={isPortalLoading && settings.subscriptionStatus !== 'trial'}
                                className="px-4 py-2 rounded-md bg-primary hover:bg-blue-600 text-white font-semibold transition-colors disabled:bg-gray-500"
                            >
                                {settings.subscriptionStatus === 'trial' ? 'Suscribirse Ahora' : (isPortalLoading ? 'Abriendo...' : 'Gestionar Suscripción')}
                            </button>
                        </div>
                    </div>

                    {/* Danger Zone */}
                    <div className="pt-6 mt-6 border-t border-red-500/30">
                        <h3 className="text-lg font-semibold text-red-500">Zona de Peligro</h3>
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-2">
                            La eliminación de tu cuenta es una acción irreversible. Se borrarán permanentemente todos tus datos, incluyendo transacciones, reglas y configuraciones.
                        </p>
                        <div className="flex justify-end mt-4">
                            <button
                                onClick={onOpenDeleteAccountModal}
                                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold transition-colors"
                            >
                                Borrar mi cuenta
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileModal;